import { decode, encode } from "utils/HelperFunctions";

const MAX_PUBLIC_LINK_LENGTH = 512;
const PREFIX_PATTERN = /^[A-Za-z][A-Za-z0-9]*_$/;
const ENCODED_VALUE_PATTERN = /^[A-Za-z0-9_-]+$/;
const NUMERIC_ID_PATTERN = /^\d+$/;
const TIMESTAMP_PATTERN = /^\d{10,16}$/;

// The link-details endpoints answer a missing record (or a slug they cannot
// read) with 400 "… not found." rather than 404, so both mean "bad link".
// Expired, cancelled and paid states come back as 200 with data, so they are
// not affected.
const PUBLIC_LINK_NOT_FOUND_STATUSES = new Set([400, 404]);

const PUBLIC_LINK_NOT_FOUND_CODES = new Set([
  "PUBLIC_LINK_NOT_FOUND",
  "INVALID_PUBLIC_LINK",
  "INVALID_SLUG",
  "NOT_FOUND",
]);

type PublicLinkParts = {
  recordId: string;
  clusterId: string;
  timestamp: string;
};

export const decodeAndValidatePublicLink = (
  slug: string | null | undefined,
  expectedPrefix?: string
): PublicLinkParts | null => {
  if (!slug || slug.length > MAX_PUBLIC_LINK_LENGTH) {
    return null;
  }

  const prefixEnd = slug.indexOf("_");
  if (prefixEnd <= 0) {
    return null;
  }

  const prefix = slug.slice(0, prefixEnd + 1);
  const encodedValue = slug.slice(prefixEnd + 1);
  if (
    !PREFIX_PATTERN.test(prefix) ||
    (expectedPrefix && prefix !== expectedPrefix) ||
    !encodedValue ||
    !ENCODED_VALUE_PATTERN.test(encodedValue)
  ) {
    return null;
  }

  try {
    const decodedValue = decode(slug);
    const [recordId, clusterId, timestamp, ...extraParts] =
      decodedValue.split("_");

    if (
      extraParts.length > 0 ||
      !NUMERIC_ID_PATTERN.test(recordId) ||
      !NUMERIC_ID_PATTERN.test(clusterId) ||
      !TIMESTAMP_PATTERN.test(timestamp) ||
      encode(decodedValue, prefix) !== slug
    ) {
      return null;
    }

    return { recordId, clusterId, timestamp };
  } catch {
    return null;
  }
};

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
  typeof value === "object" && value !== null
    ? (value as UnknownRecord)
    : null;

export const isInvalidPublicLinkResult = (value: unknown): boolean => {
  const root = asRecord(value);
  const response = asRecord(root?.response);
  const responseData = asRecord(response?.data);
  const responseBody = asRecord(responseData?.body);
  const rootBody = asRecord(root?.body);
  const rootData = asRecord(root?.data);
  const rootBodyData = asRecord(rootBody?.data);
  const responseBodyData = asRecord(responseBody?.data);
  const nestedError = asRecord(
    responseBody?.error ||
      responseData?.error ||
      rootBody?.error ||
      rootData?.error ||
      root?.error
  );

  const statuses = [
    response?.status,
    responseData?.status,
    responseBody?.status,
    root?.status,
    rootBody?.status,
    rootData?.status,
    rootBody?.httpStatus,
    responseBody?.httpStatus,
  ];
  if (
    statuses.some((status) => PUBLIC_LINK_NOT_FOUND_STATUSES.has(Number(status)))
  ) {
    return true;
  }

  // The portal API answers a missing or expired link with 200 and
  // `success: false` ("Invalid or expired agreement link."). A server error
  // reported in the body is not the link's fault, so it is left to the page.
  if (
    rootBody?.success === false &&
    !statuses.some((status) => Number(status) >= 500)
  ) {
    return true;
  }

  if (
    typeof root?.message === "string" &&
    /status code 40[04](?:\D|$)/i.test(root.message)
  ) {
    return true;
  }

  const codes = [
    responseBody?.code,
    responseData?.code,
    rootBody?.code,
    rootData?.code,
    root?.code,
    rootBodyData?.code,
    responseBodyData?.code,
    nestedError?.code,
  ];

  return codes.some(
    (code) =>
      typeof code === "string" &&
      PUBLIC_LINK_NOT_FOUND_CODES.has(code.toUpperCase())
  );
};
